import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { ZnglistTemplateTypeEnum } from '@/graphql/types';
import { getZnglistTemplateInfo } from '@/routes/dash/znglists/-lib/template/DefaultSelectTemplate';
import React, { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useFilterBarCtx } from '../FilterBar';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const znglistTemplateSearchSchema = z.object({
  znglistTemplate: z.array(z.nativeEnum(ZnglistTemplateTypeEnum)).nullish(),
});

const znglistTemplateComponentProps = z.object({
  displayName: z.string().optional(),
});

type ZnglistTemplateFilterProps = z.infer<typeof znglistTemplateComponentProps>;

const znglistTemplateOptions = Object.values(ZnglistTemplateTypeEnum)
  .map((template) => {
    const templateInfo = getZnglistTemplateInfo(template);
    return {
      value: template,
      displayName: templateInfo.prettyType,
    };
  })
  .sort((a, b) => {
    if (a.value === ZnglistTemplateTypeEnum.Pending) return 1;
    if (b.value === ZnglistTemplateTypeEnum.Pending) return -1;
    return a.displayName.localeCompare(b.displayName);
  });

const ZnglistTemplateFilter = ({ displayName = 'Template' }: ZnglistTemplateFilterProps) => {
  const { props: ctxProps } = useFilterBarCtx();

  const { fastFilters, slowFilters, updateFilter, isUpdating } = __useFilterState(znglistTemplateSearchSchema);

  useEffect(() => {
    const selectedTemplates = slowFilters?.znglistTemplate;
    if (!selectedTemplates) return;

    const allowedTemplates = znglistTemplateOptions.map((option) => option.value);

    const validTemplates = selectedTemplates.filter((template) => allowedTemplates.includes(template));

    if (validTemplates.length !== selectedTemplates.length) {
      updateFilter({
        znglistTemplate: validTemplates.length ? validTemplates : undefined,
      });
    }
  }, [slowFilters.znglistTemplate, updateFilter]);

  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    return znglistTemplateOptions.map(({ value, displayName }) => ({
      name: displayName,
      value,
      onClick: () => {
        updateFilter((prev) => {
          let newTemplates = [...(prev?.znglistTemplate || []), value];
          if (prev?.znglistTemplate?.includes(value)) {
            newTemplates = prev.znglistTemplate.filter((template) => template !== value);
          }

          return {
            znglistTemplate: newTemplates.length ? newTemplates : undefined,
          };
        });
      },
    }));
  }, [updateFilter]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className={ctxProps?.variant === 'default' ? 'z-btn-6' : 'z-btn-compact'}
        name={displayName}
        menuList={menuItems}
        type="multiple-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'znglistTemplate',
          isSearchable: false,
        }}
      />
      <FilterBadge count={fastFilters?.znglistTemplate?.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const ZnglstTemplateMemo = React.memo(ZnglistTemplateFilter);

export const FilterConfig_ZnglistTemplate = {
  znglistTemplate: {
    searchSchema: znglistTemplateSearchSchema,
    component: ZnglstTemplateMemo,
    componentProps: znglistTemplateComponentProps,
  },
} as const;
